import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { ArrowUp, Check, ChevronDown, Mic, PanelLeft, Plus, Search, Share2, SlidersHorizontal, SquarePen } from 'lucide-react';
import { BrandLogo } from './AppChrome';
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png';

/**
 * The conversation, as a sequence of exchanges rather than swappable variants:
 * ChatGPT is a transcript, so the beats append downward and the thread scrolls
 * to follow — which is what makes it read as the real surface rather than a
 * card that re-renders in place.
 */
type Beat = {
  prompt: string;
  /** The connector call ChatGPT shows above an answer that used a tool. */
  call: string;
  detail: string;
  lead: string;
  body: React.ReactNode;
};
const AD_SETS = [{
  name: 'Ad set 1 · Broad',
  roas: '4.2×',
  cpa: '$18',
  good: true
}, {
  name: 'Ad set 2 · Lookalike',
  roas: '1.1×',
  cpa: '$72',
  good: false
}, {
  name: 'Ad set 3 · Interests',
  roas: '0.9×',
  cpa: '$88',
  good: false
}, {
  name: 'Ad set 4 · Retargeting',
  roas: '3.8×',
  cpa: '$21',
  good: true
}];
function AdSetTable() {
  return <div className="overflow-hidden rounded-lg border border-[#E5E5E5] text-[13px]" data-id="element-307" data-mp="3aaynfetgp8a2">
      <div className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-[#E5E5E5] bg-[#F9F9F9] text-[11px] font-semibold text-[#5D5D5D]" data-id="element-308" data-mp="3a93yzqizey18">
        <span className="px-3 py-2" data-id="element-309" data-mp="3j41w79t5wwyd">Ad set</span>
        <span className="px-3 py-2 text-right" data-id="element-310" data-mp="3j5czl8ip42oy">ROAS</span>
        <span className="px-3 py-2 text-right" data-id="element-311" data-mp="1u3sqmmtiu72">CPA</span>
      </div>
      {AD_SETS.map(r => <div key={r.name} className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-[#F0F0F0] last:border-0" data-id="element-312" data-mp="3t0wpneakw75s">
          <span className="truncate px-3 py-2 text-[#0D0D0D]" data-id="element-313" data-mp="23njilhb3050z">{r.name}</span>
          <span className={`px-3 py-2 text-right ${r.good ? 'text-[#0D0D0D]' : 'font-semibold text-[#C2410C]'}`} data-id="element-314" data-mp="1ynyk17ztpyos">
            {r.roas}
          </span>
          <span className="px-3 py-2 text-right text-[#5D5D5D]" data-id="element-315" data-mp="2oddnw2z0j8w0">{r.cpa}</span>
        </div>)}
    </div>;
}
const BEATS: Beat[] = [{
  prompt: 'How did our Meta ads do last week?',
  call: 'Giga · Meta Ads',
  detail: 'Read 4 ad sets, last 7 days',
  lead: 'Here is last week across your four active ad sets.',
  body: <div className="flex flex-col gap-3" data-id="element-316" data-mp="32992vxqajzu4">
      <AdSetTable data-id="element-317" data-mp="37b8719tuv0s4" />
      <p data-id="element-318" data-mp="379nbphzpvdt7">Ad sets 2 and 3 are well under your 2× ROAS target — worth a closer look.</p>
    </div>
}, {
  prompt: 'Which should we cut, and where should the budget go?',
  call: 'Giga · Company Brain',
  detail: 'Matched your ROAS and CPA targets',
  lead: 'Weighed against the targets in your growth track:',
  body: <ul className="flex list-disc flex-col gap-1 pl-5" data-id="element-319" data-mp="3l1bntl9yu2a6">
      <li data-id="element-320" data-mp="3q1g69gufp731">
        Pause <strong className="font-semibold" data-id="element-321" data-mp="3q1zci0aq2718">ad set 2</strong> — 1.1× ROAS, $72 CPA
      </li>
      <li data-id="element-322" data-mp="3uz7x07jcen5n">
        Pause <strong className="font-semibold" data-id="element-323" data-mp="3v0srjx5nq63i">ad set 3</strong> — 0.9× ROAS, $88 CPA
      </li>
      <li data-id="element-324" data-mp="1jdp8ik9a99b6">Move that budget to ad sets 1 &amp; 4, your top performers</li>
    </ul>
}, {
  prompt: 'Go ahead — and let the team know in Slack.',
  call: 'Giga · Meta Ads, Slack',
  detail: '4 changes applied · posted to #growth',
  lead: 'Done. Changes are live in Meta Ads Manager:',
  body: <div className="flex flex-col gap-3" data-id="element-325" data-mp="14fwy26ilfb3u">
      <ul className="flex flex-col gap-1.5" data-id="element-326" data-mp="14h7z3yknm4r9">
        {['Paused ad set 2', 'Paused ad set 3', 'Ad set 1 budget +40%', 'Ad set 4 budget +40%'].map(t => <li key={t} className="flex items-center gap-2" data-id="element-327" data-mp="1x4caclacbfbh">
            <Check size={14} strokeWidth={2.6} className="shrink-0 text-[#059669]" data-id="element-328" data-mp="1x4uaht04idoo" />
            {t}
          </li>)}
      </ul>
      <blockquote className="border-l-2 border-[#E5E5E5] pl-3 text-[#5D5D5D]" data-id="element-329" data-mp="1ne96fq1okgyd">
        Heads up team — pausing ad sets 2 &amp; 3 (ROAS under 1×) and moving budget to 1 &amp; 4.
      </blockquote>
    </div>
}];

/** The tool-call disclosure ChatGPT renders above an answer that used a connector. */
function ConnectorCall({
  call,
  detail,
  working
}: {
  call: string;
  detail: string;
  working: boolean;
}) {
  return <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border border-[#E5E5E5] bg-[#F9F9F9] px-2.5 py-1.5" data-id="element-330" data-mp="25kjbc7s5ecom">
      <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[15px] shrink-0 rounded-[4px]" data-id="element-331" data-mp="1qo0w8bgxf8k8" />
      <span className="truncate text-[12.5px] font-medium text-[#0D0D0D]" data-id="element-332" data-mp="1vmc8stylvx65">{call}</span>
      <span className="hidden truncate text-[12px] text-[#8F8F8F] sm:inline" data-id="element-333" data-mp="2jcg2o9lzsijy">
        {working ? 'Working…' : detail}
      </span>
      <ChevronDown size={13} className="shrink-0 text-[#B4B4B4]" data-id="element-334" data-mp="2ockhzwb5z78l" />
    </div>;
}
const SIDEBAR_CHATS = ['Meta ads — last week', 'Q3 pipeline review', 'Renewal risk summary', 'Landing page copy'];

/**
 * ChatGPT's own chrome, rebuilt: collapsible sidebar with recents, the model
 * name as a dropdown in the header, an unbubbled assistant response, and the
 * pill composer. Previously this screen was a bare card holding two bubbles,
 * which is why it did not read as ChatGPT — the bubbles were right, but none of
 * the surrounding surface that identifies the product was there.
 */
export function ChatGPTPanel({
  className = '',
  heightClass = 'h-[560px]'
}: {
  className?: string;
  heightClass?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, {
    amount: 0.35
  });
  // How many exchanges have landed, and whether the newest is still working.
  const [shown, setShown] = useState(1);
  const [working, setWorking] = useState(false);
  useEffect(() => {
    if (!inView) return;
    setShown(1);
    setWorking(true);
  }, [inView]);
  useEffect(() => {
    if (!inView) return;
    if (working) {
      const id = setTimeout(() => setWorking(false), 1500);
      return () => clearTimeout(id);
    }
    if (shown >= BEATS.length) return;
    const id = setTimeout(() => {
      setShown(s => s + 1);
      setWorking(true);
    }, 2600);
    return () => clearTimeout(id);
  }, [inView, working, shown]);
  // Follow the transcript as it grows, the way the real thread does.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    });
  }, [shown, working]);
  return <div ref={ref} className={`overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white text-left shadow-[0_1px_2px_rgba(43,33,26,0.05),0_8px_24px_-12px_rgba(43,33,26,0.18),0_40px_100px_-40px_rgba(43,33,26,0.45)] ${className}`} data-id="element-335" data-mp="13p88koq8jamy">
      <div className={`flex ${heightClass}`} data-id="element-336" data-mp="bwzd9o65soby">
        {/* Sidebar */}
        <aside className="hidden w-[212px] shrink-0 flex-col bg-[#F9F9F9] px-2 py-3 sm:flex" data-id="element-337" data-mp="fzvpmx53vo7g">
          <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-338" data-mp="ks70uw5aqwfb">
            <BrandLogo slug="chatgpt" className="size-[18px]" data-id="element-339" data-mp="3kff1q3ramhbr" />
            <PanelLeft size={15} className="text-[#B4B4B4]" data-id="element-340" data-mp="3kd2kjstd0azr" />
          </div>
          {[{
          icon: SquarePen,
          label: 'New chat'
        }, {
          icon: Search,
          label: 'Search chats'
        }].map(item => <div key={item.label} className="flex items-center gap-2.5 rounded-lg px-2 py-[7px]" data-id="element-341" data-mp="3bxd1ur4o0b57">
              <item.icon size={15} strokeWidth={1.9} className="text-[#5D5D5D]" data-id="element-342" data-mp="37vrsvgv94h0a" />
              <span className="text-[13.5px] text-[#0D0D0D]" data-id="element-343" data-mp="32x6qbasj1l8o">{item.label}</span>
            </div>)}
          <p className="mb-1 mt-4 px-2 text-[12px] font-medium text-[#8F8F8F]" data-id="element-344" data-mp="2xy3kho4q3azn">Chats</p>
          <ul className="flex flex-col" data-id="element-345" data-mp="2xzy85a7dph7f">
            {SIDEBAR_CHATS.map((c, i) => <li key={c} className={`truncate rounded-lg px-2 py-[6px] text-[13px] ${i === 0 ? 'bg-[#EDEDED] font-medium text-[#0D0D0D]' : 'text-[#5D5D5D]'}`} data-id="element-346" data-mp="2t5u0spu7b75t" data-mpi={i}>
                {c}
              </li>)}
          </ul>
          <div className="mt-auto flex items-center gap-2.5 rounded-lg px-2 py-1.5" data-id="element-347" data-mp="2k8jl8sacovv3">
            <span className="flex size-[22px] items-center justify-center rounded-full bg-[#0D8B6C] text-[10px] font-semibold text-white" data-id="element-348" data-mp="2f72ptcamy1lw">
              SC
            </span>
            <span className="truncate text-[13px] text-[#0D0D0D]" data-id="element-349" data-mp="20w30n4fw37l">Sarah Chen</span>
          </div>
        </aside>

        {/* Conversation */}
        <div className="flex min-w-0 flex-1 flex-col bg-white" data-id="element-350" data-mp="6wthenw56zhq">
          <header className="flex shrink-0 items-center justify-between px-4 py-3" data-id="element-351" data-mp="3p3r1z7em2beu">
            <span className="inline-flex items-center gap-1 text-[15px] font-medium text-[#0D0D0D]" data-id="element-352" data-mp="3u2u8kwa8opox">
              ChatGPT
              <span className="text-[#8F8F8F]" data-id="element-353" data-mp="3fd67nw5yzt1q">5</span>
              <ChevronDown size={15} className="text-[#8F8F8F]" data-id="element-354" data-mp="3k5643emu03tk" />
            </span>
            <span className="flex items-center gap-3 text-[#5D5D5D]" data-id="element-355" data-mp="36e1aat65a5r5">
              <Share2 size={16} strokeWidth={1.9} data-id="element-356" data-mp="36fvto31rppt4" />
              <SlidersHorizontal size={16} strokeWidth={1.9} data-id="element-357" data-mp="3af4lh2d8zdm1" />
            </span>
          </header>

          <div ref={threadRef} className="flex-1 overflow-hidden px-4 pb-2 sm:px-6" data-id="element-358" data-mp="2wfbst1i5unbf">
            <div className="mx-auto flex max-w-[620px] flex-col gap-6 py-2 text-[15px] leading-[1.65] text-[#0D0D0D]" data-id="element-359" data-mp="31eomo67g8uni">
              {BEATS.slice(0, shown).map((beat, i) => {
              const isLatest = i === shown - 1;
              const pending = isLatest && working;
              return <div key={beat.prompt} className="flex flex-col gap-4" data-id="element-360" data-mp="2cwfsfladdoty" data-mpi={i}>
                    <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="max-w-[85%] self-end rounded-[22px] bg-[#F4F4F4] px-4 py-2.5" data-id="element-361" data-mp="2rsngwxst9cf1">
                      {beat.prompt}
                    </motion.div>
                    <div className="flex flex-col gap-3" data-id="element-362" data-mp="35i7elp7jbjhh">
                      <ConnectorCall call={beat.call} detail={beat.detail} working={pending} data-id="element-363" data-mp="35ktt68suez97" />
                      <AnimatePresence mode="wait" data-id="element-364" data-mp="3jgfgmbjfhazi">
                        {pending ? <motion.div key="working" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.15
                    }} className="flex items-center gap-1.5 py-1" data-id="element-365" data-mp="3fhyanvbqu6jo">
                            {[0, 1, 2].map(d => <motion.span key={d} className="size-[6px] rounded-full bg-[#B4B4B4]" animate={{
                        opacity: [0.3, 1, 0.3]
                      }} transition={{
                        duration: 1.1,
                        repeat: Infinity,
                        delay: d * 0.16,
                        ease: 'linear'
                      }} data-id="element-366" data-mp="32ul6k2ecrnyj" />)}
                          </motion.div> : <motion.div key="answer" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      duration: 0.22
                    }} className="flex flex-col gap-3" data-id="element-367" data-mp="3mikihgknl15j">
                            <p data-id="element-368" data-mp="qle8l3n8lj6x">{beat.lead}</p>
                            {beat.body}
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Composer */}
          <div className="shrink-0 px-4 pb-4 sm:px-6" data-id="element-369" data-mp="14bq3tbf4by2a">
            <div className="mx-auto flex max-w-[620px] items-center gap-2 rounded-[26px] border border-[#E5E5E5] bg-white px-3 py-2.5 shadow-[0_2px_8px_rgba(13,13,13,0.05)]" data-id="element-370" data-mp="14daw0udyn4wz">
              <Plus size={18} strokeWidth={1.9} className="shrink-0 text-[#5D5D5D]" data-id="element-371" data-mp="1e8uqdccvlvjm" />
              <span className="min-w-0 flex-1 truncate text-[15px] text-[#8F8F8F]" data-id="element-372" data-mp="1ibj15pl5hcj8">Ask anything</span>
              <Mic size={17} strokeWidth={1.9} className="shrink-0 text-[#5D5D5D]" data-id="element-373" data-mp="1n9swq1asef5d" />
              <span className="flex size-[28px] shrink-0 items-center justify-center rounded-full bg-[#0D0D0D]" data-id="element-374" data-mp="3k3nm2h48f9kg">
                <ArrowUp size={15} strokeWidth={2.4} className="text-white" data-id="element-375" data-mp="3p3a18w3mezvw" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}